import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Sparkles, ChevronRight, Loader2 } from "lucide-react";
import { api, thumbUrl } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/hooks/useFavorites";
import { StickerGrid } from "@/components/StickerGrid";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function SeasonalHero({ packs, onOpen }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (packs.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % packs.length), 5000);
    return () => clearInterval(t);
  }, [packs.length]);
  if (!packs || packs.length === 0) return null;
  const p = packs[idx];
  const cover = p.cover_sticker ? thumbUrl(p.cover_sticker.storage_path) : null;
  return (
    <button
      data-testid="seasonal-hero"
      onClick={() => onOpen(p.id)}
      className="w-full text-left rounded-[2rem] overflow-hidden border border-[#E8CFD1] bc-shadow-hover animate-float-up relative"
      style={{ background: "linear-gradient(120deg,#C58B90 0%,#D6A0A4 45%,#E8CFD1 100%)" }}
    >
      <div className="flex items-center gap-4 p-5 sm:p-7">
        <div className="flex-1">
          <span className="inline-block text-[10px] font-bold uppercase tracking-[0.2em] text-white/90 bg-white/20 px-3 py-1 rounded-full mb-2">
            {p.season_label || "Coleção Sazonal"}
          </span>
          <h2 className="font-serif text-2xl sm:text-3xl text-white leading-tight">
            {p.emoji} {p.name}
          </h2>
          {p.description && <p className="text-sm text-white/85 mt-1 line-clamp-2 max-w-md">{p.description}</p>}
          <span className="inline-flex items-center gap-1 mt-3 text-sm font-semibold text-white bg-[#2C2A29]/25 px-4 py-2 rounded-full">
            Ver coleção <ChevronRight size={16} />
          </span>
        </div>
        <div className="h-24 w-24 sm:h-32 sm:w-32 rounded-3xl bg-white/25 backdrop-blur flex items-center justify-center shrink-0">
          {cover ? (
            <img src={cover} alt={p.name} className="h-[85%] object-contain drop-shadow" />
          ) : (
            <span className="text-5xl">{p.emoji}</span>
          )}
        </div>
      </div>
      {packs.length > 1 && (
        <div className="absolute bottom-3 left-6 flex gap-1.5">
          {packs.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-white" : "w-1.5 bg-white/50"}`} />
          ))}
        </div>
      )}
    </button>
  );
}

function PackCover({ pack, onClick }) {
  const cover = pack.cover_sticker ? thumbUrl(pack.cover_sticker.storage_path) : null;
  return (
    <button
      data-testid={`home-pack-${pack.id}`}
      onClick={onClick}
      className="snap-start shrink-0 w-56 text-left"
    >
      <div className="relative h-40 rounded-3xl overflow-hidden border border-[#F2E6E7] bc-shadow-soft sticker-canvas flex items-center justify-center transition-transform duration-300 hover:-translate-y-1">
        {pack.featured && (
          <span className="absolute top-3 left-3 z-10 bg-[#C58B90] text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-widest">
            Destaque
          </span>
        )}
        {cover ? (
          <img src={cover} alt={pack.name} className="h-[78%] object-contain drop-shadow" />
        ) : (
          <span className="text-5xl">{pack.emoji}</span>
        )}
      </div>
      <p className="mt-2.5 font-serif text-lg text-[#2C2A29]">
        {pack.emoji} {pack.name}
      </p>
      <p className="text-xs text-[#7A7571]">{pack.sticker_count} stickers</p>
    </button>
  );
}

export function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { favIds, onToggleFav } = useFavorites();
  const [categories, setCategories] = useState([]);
  const [packs, setPacks] = useState([]);
  const [seasonal, setSeasonal] = useState([]);
  const [stickers, setStickers] = useState([]);
  const [notice, setNotice] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/categories"),
      api.get("/packs", { params: { featured: true } }),
      api.get("/stickers", { params: { limit: 15 } }),
      api.get("/notices/active"),
      api.get("/packs", { params: { seasonal: true, active: true } }),
    ])
      .then(([c, p, s, n, sp]) => {
        setCategories(c.data);
        setPacks(p.data);
        setStickers(s.data.slice(0, 15));
        setNotice(n.data);
        setSeasonal(sp.data);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const term = query.trim();
    if (!term) {
      setResults(null);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      api
        .get("/stickers", { params: { search: term, limit: 120 } })
        .then((res) => setResults(res.data))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const firstName = useMemo(() => (user?.name || "").split(" ")[0], [user]);

  if (loading) {
    return (
      <div className="py-24 flex justify-center">
        <Loader2 className="animate-spin text-[#C58B90]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="animate-float-up">
        <p className="text-sm text-[#7A7571]">{greeting()},</p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[#2C2A29]">{firstName || "linda"} 🎀</h1>
      </div>

      <div className="relative animate-float-up" style={{ animationDelay: "60ms" }}>
        <Search size={19} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C58B90]" />
        <input
          data-testid="home-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por nome ou palavra-chave (ex: mãe, café)…"
          className="w-full h-13 py-3.5 pl-12 pr-4 rounded-full bg-white border border-[#E8CFD1] outline-none focus:border-[#C58B90] focus:ring-2 focus:ring-[#F2E6E7] transition-[border-color,box-shadow] text-sm"
        />
      </div>

      {results !== null ? (
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-2xl">Resultados</h2>
            {searching && <Loader2 className="animate-spin text-[#C58B90]" size={18} />}
          </div>
          <StickerGrid
            stickers={results}
            favIds={favIds}
            onToggleFav={onToggleFav}
            emptyText={`Nada encontrado para "${query}". Tente outra palavra 💗`}
          />
        </section>
      ) : (
        <>
          {notice && (
            <div
              data-testid="home-notice"
              className="rounded-3xl px-5 py-4 flex items-center gap-3 animate-float-up"
              style={{ background: "linear-gradient(120deg,#F2E6E7,#E8CFD1)", animationDelay: "120ms" }}
            >
              <div className="h-10 w-10 rounded-2xl bg-white/70 flex items-center justify-center shrink-0">
                <Sparkles size={18} className="text-[#C58B90]" />
              </div>
              <p className="text-sm text-[#8A5F63] font-medium">{notice.message}</p>
            </div>
          )}

          <SeasonalHero packs={seasonal} onOpen={(id) => navigate(`/packs/${id}`)} />

          {packs.length > 0 && (
            <section className="animate-float-up" style={{ animationDelay: "160ms" }}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-2xl">Coleções em destaque</h2>
                <button
                  data-testid="home-see-packs"
                  onClick={() => navigate("/packs")}
                  className="text-sm text-[#C58B90] font-medium flex items-center gap-0.5"
                >
                  Ver todos <ChevronRight size={16} />
                </button>
              </div>
              <div className="flex gap-4 overflow-x-auto scrollbar-hide snap-x pb-2 -mx-1 px-1">
                {packs.map((p) => (
                  <PackCover key={p.id} pack={p} onClick={() => navigate(`/packs/${p.id}`)} />
                ))}
              </div>
            </section>
          )}

          <section className="animate-float-up" style={{ animationDelay: "200ms" }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-serif text-2xl">Categorias</h2>
              <button
                data-testid="home-see-categories"
                onClick={() => navigate("/categorias")}
                className="text-sm text-[#C58B90] font-medium flex items-center gap-0.5"
              >
                Ver todas <ChevronRight size={16} />
              </button>
            </div>
            <div className="flex gap-2.5 overflow-x-auto scrollbar-hide snap-x pb-2 -mx-1 px-1">
              {categories.map((c) => (
                <button
                  key={c.id}
                  data-testid={`home-chip-${c.id}`}
                  onClick={() => navigate(`/categorias/${c.id}`)}
                  className="snap-start shrink-0 h-11 px-5 rounded-full bg-white border border-[#E8CFD1] text-sm font-medium text-[#2C2A29] transition-colors hover:bg-[#F2E6E7] whitespace-nowrap"
                >
                  {c.emoji} {c.name}
                </button>
              ))}
            </div>
          </section>

          <section className="animate-float-up" style={{ animationDelay: "240ms" }}>
            <h2 className="font-serif text-2xl mb-4">Novidades ✨</h2>
            <StickerGrid stickers={stickers} favIds={favIds} onToggleFav={onToggleFav} />
          </section>
        </>
      )}
    </div>
  );
}
